import { useEffect, useState } from 'react';
import { User as UserIcon, X } from 'lucide-react';

/**
 * Small clickable thumbnail of a person's reception photo. Falls back
 * to a gradient initials chip when no photo exists — the gradient is
 * seeded from the name hash so each person looks distinct.
 *
 * Tapping opens a centered lightbox with the larger photo so station
 * staff can confirm identity at a glance. The lightbox is a centered
 * modal rather than an inline popover so it escapes any
 * `overflow-hidden` list / drawer that wraps the thumbnail.
 */
export type AvatarSize = 'sm' | 'md' | 'lg';

const SIZE_PX: Record<AvatarSize, number> = {
  sm: 28,
  md: 40,
  lg: 56,
};

export function PersonAvatar({
  fullName,
  photoKey,
  size = 'sm',
  className,
}: {
  fullName: string;
  photoKey: string | null | undefined;
  size?: AvatarSize;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const px = SIZE_PX[size];

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const initials = getInitials(fullName);
  const hasPhoto = Boolean(photoKey);

  // Gradient seed from name hash — keeps the same person consistently
  // tinted across pages while different people read distinct.
  const hash = Array.from(fullName).reduce((a, c) => a + c.charCodeAt(0), 0);
  const hue1 = hash % 360;
  const hue2 = (hue1 + 60) % 360;
  const gradient = `linear-gradient(135deg, hsl(${hue1} 65% 58%) 0%, hsl(${hue2} 65% 62%) 100%)`;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen(true);
        }}
        aria-label={hasPhoto ? `View photo of ${fullName}` : `No photo for ${fullName}`}
        className={[
          'block flex-none overflow-hidden rounded-full ring-1 ring-line-strong/15 transition hover:ring-accent/40 focus:outline-none focus:ring-2 focus:ring-ring-strong',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        style={{
          width: px,
          height: px,
          background: hasPhoto ? 'rgb(var(--surface-2))' : gradient,
        }}
      >
        {hasPhoto ? (
          <img
            src={`/api/photos/${photoKey}`}
            alt={fullName}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <span
            className="flex h-full w-full items-center justify-center text-[10px] font-semibold text-white drop-shadow-sm"
            aria-hidden
          >
            {initials || <UserIcon className="h-3.5 w-3.5" />}
          </span>
        )}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Photo of ${fullName}`}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-fg/60 p-4 backdrop-blur-sm aurora-fade-in"
          onClick={() => setOpen(false)}
        >
          <div
            className="relative max-w-md overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-center justify-between gap-2 border-b border-divider/10 px-4 py-2.5">
              <span className="truncate text-sm font-semibold text-fg">
                {fullName}
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="rounded-token-sm p-1 text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg"
              >
                <X className="h-4 w-4" />
              </button>
            </header>
            {hasPhoto ? (
              <img
                src={`/api/photos/${photoKey}`}
                alt={fullName}
                className="block max-h-[70vh] w-auto"
              />
            ) : (
              <div className="flex h-64 w-72 flex-col items-center justify-center gap-3 text-fg-muted">
                <div
                  className="flex h-20 w-20 items-center justify-center rounded-full ring-1 ring-line/15"
                  style={{ background: gradient }}
                >
                  <span className="text-xl font-semibold text-white drop-shadow-sm">
                    {initials || <UserIcon className="h-8 w-8" />}
                  </span>
                </div>
                <p className="text-sm">No photo on file yet</p>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
